/**
 * IeContentModal.tsx — legacy's "Document Content" editor
 * (admin_procedures_ie_documents.php) for accounts with imported I&E content:
 * a sub-category's Inquiry, or a procedure's Risk, Pre-Op, Post-Op, As You
 * Heal and Special content. Content is sections, each with paragraphs (Title,
 * Bold title, Display box, Italic content, Content). The legacy I&E forms
 * print it into patient documents.
 *
 * Saves the whole content at once; sections and paragraphs taken out are
 * removed (legacy's own removals never reached the database).
 *
 * On a Locked page AdminFormModal makes it a viewer (fields read-only, Close
 * only); this editor also drops its move, remove and add buttons. The content
 * tabs still switch.
 */
import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { Textarea } from '@/components/ui/Textarea';
import { getErrorMessage } from '@/services/apiClient';
import {
  IE_TYPES,
  getIeContent,
  saveIeContent,
  type IeParagraph,
  type IeSection,
  type IeType,
} from '@/services/proceduresAdmin.service';
import { toast } from '@/store/toastStore';

function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

const BLANK_PARAGRAPH: IeParagraph = { id: 0, title: '', bold: false, box: false, italic: false, content: '' };

export function IeContentModal({
  types,
  ownerId,
  ownerName,
  onClose,
}: {
  /** The content kinds this owner has: [1] for a sub-category, 2–6 for a procedure. */
  types: IeType[];
  ownerId: number;
  ownerName: string;
  onClose: () => void;
}) {
  const locked = useAdminPageLevel() === 'locked';
  const [type, setType] = useState<IeType>(types[0]);
  const [saved, setSaved] = useState<IeSection[] | null>(null);
  const [sections, setSections] = useState<IeSection[]>([]);
  const [saving, setSaving] = useState(false);
  const dirty = saved !== null && JSON.stringify(sections) !== JSON.stringify(saved);

  useEffect(() => {
    let cancelled = false;
    setSaved(null);
    getIeContent(type, ownerId)
      .then((rows) => {
        if (cancelled) return;
        setSaved(rows);
        setSections(rows);
      })
      .catch((err) => {
        if (!cancelled) toast.error(getErrorMessage(err, 'The document content could not be loaded.'));
      });
    return () => {
      cancelled = true;
    };
  }, [type, ownerId]);

  function patchSection(i: number, patch: Partial<IeSection>) {
    setSections((prev) => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  }

  function patchParagraph(si: number, pi: number, patch: Partial<IeParagraph>) {
    setSections((prev) =>
      prev.map((s, j) => (j === si ? { ...s, paragraphs: s.paragraphs.map((p, k) => (k === pi ? { ...p, ...patch } : p)) } : s)),
    );
  }

  async function save() {
    setSaving(true);
    try {
      const rows = await saveIeContent(type, ownerId, sections);
      setSaved(rows);
      setSections(rows);
      toast.success('Document content saved');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The document content could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  const label = IE_TYPES.find((t) => t.type === type)?.label ?? '';

  return (
    <AdminFormModal
      title={`Document Content: ${label}`}
      description={ownerName}
      isNew={false}
      dirty={dirty}
      saving={saving}
      saveLabel="Save content"
      onSave={() => void save()}
      onRevertAll={() => saved && setSections(saved)}
      onClose={onClose}
      size="xl"
    >
      {/* The gap between the tabs and the sections, as the other Admin forms. */}
      <div className="space-y-4">
        {types.length > 1 && (
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Content">
            {types.map((t) => (
              <Button
                key={t}
                role="tab"
                aria-selected={t === type}
                size="sm"
                variant={t === type ? 'primary' : 'secondary'}
                disabled={dirty && t !== type}
                title={dirty && t !== type ? 'Save or undo your changes first' : undefined}
                onClick={() => setType(t)}
              >
                {IE_TYPES.find((x) => x.type === t)?.label}
              </Button>
            ))}
          </div>
        )}

        {saved === null ? (
          <div className="relative h-32">
            <SpinnerOverlay label="Loading content…" />
          </div>
        ) : (
          <div className="space-y-4" data-testid="ie-sections">
            {sections.length === 0 && <p className="text-sm text-slate-500">No content yet.</p>}
            {sections.map((s, si) => (
              <div key={s.id || `new-${si}`} className="rounded-md border border-slate-200 p-3" data-testid="ie-section">
                <div className="flex items-center gap-2">
                  <Input
                    aria-label={`Section ${si + 1} name`}
                    placeholder="Section name"
                    value={s.name}
                    maxLength={255}
                    onChange={(e) => patchSection(si, { name: e.target.value })}
                    wrapperClassName="flex-1"
                  />
                  {!locked && (
                    <>
                      <Button variant="ghost" size="sm" aria-label={`Move section ${si + 1} up`} leftIcon={<ArrowUp className="h-4 w-4" />} disabled={si === 0} onClick={() => setSections((p) => move(p, si, si - 1))} />
                      <Button variant="ghost" size="sm" aria-label={`Move section ${si + 1} down`} leftIcon={<ArrowDown className="h-4 w-4" />} disabled={si === sections.length - 1} onClick={() => setSections((p) => move(p, si, si + 1))} />
                      <Button variant="ghost" size="sm" aria-label={`Remove section ${si + 1}`} leftIcon={<Trash2 className="h-4 w-4" />} onClick={() => setSections((p) => p.filter((_, j) => j !== si))} />
                    </>
                  )}
                </div>

                <div className="mt-3 space-y-3 border-l-2 border-slate-100 pl-3">
                  {s.paragraphs.map((p, pi) => (
                    <div key={p.id || `new-${pi}`} className="space-y-2" data-testid="ie-paragraph">
                      <div className="flex items-center gap-2">
                        <Input
                          aria-label={`Section ${si + 1} paragraph ${pi + 1} title`}
                          placeholder="Paragraph title"
                          value={p.title}
                          maxLength={255}
                          onChange={(e) => patchParagraph(si, pi, { title: e.target.value })}
                          wrapperClassName="flex-1"
                        />
                        {!locked && (
                          <>
                            <Button variant="ghost" size="sm" aria-label={`Move paragraph ${pi + 1} up`} leftIcon={<ArrowUp className="h-4 w-4" />} disabled={pi === 0} onClick={() => patchSection(si, { paragraphs: move(s.paragraphs, pi, pi - 1) })} />
                            <Button variant="ghost" size="sm" aria-label={`Move paragraph ${pi + 1} down`} leftIcon={<ArrowDown className="h-4 w-4" />} disabled={pi === s.paragraphs.length - 1} onClick={() => patchSection(si, { paragraphs: move(s.paragraphs, pi, pi + 1) })} />
                            <Button variant="ghost" size="sm" aria-label={`Remove paragraph ${pi + 1} of section ${si + 1}`} leftIcon={<Trash2 className="h-4 w-4" />} onClick={() => patchSection(si, { paragraphs: s.paragraphs.filter((_, k) => k !== pi) })} />
                          </>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-4">
                        <Checkbox label="Bold title" checked={p.bold} onChange={(e) => patchParagraph(si, pi, { bold: e.target.checked })} />
                        <Checkbox label="Display box" checked={p.box} onChange={(e) => patchParagraph(si, pi, { box: e.target.checked })} />
                        <Checkbox label="Italic content" checked={p.italic} onChange={(e) => patchParagraph(si, pi, { italic: e.target.checked })} />
                      </div>
                      <Textarea
                        aria-label={`Section ${si + 1} paragraph ${pi + 1} content`}
                        rows={3}
                        value={p.content}
                        onChange={(e) => patchParagraph(si, pi, { content: e.target.value })}
                      />
                    </div>
                  ))}
                  {!locked && (
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Plus className="h-4 w-4" />}
                      onClick={() => patchSection(si, { paragraphs: [...s.paragraphs, { ...BLANK_PARAGRAPH }] })}
                    >
                      Add paragraph
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {!locked && (
              <Button
                variant="secondary"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setSections((p) => [...p, { id: 0, name: '', paragraphs: [] }])}
              >
                Add section
              </Button>
            )}
          </div>
        )}
      </div>
    </AdminFormModal>
  );
}
